import { Rechnung } from "./models/rechnung"
import { useSyncExternalStore } from "react"

type Listener = () => void

export let rechnung = new Rechnung()
let result = { rechnung }

export function setRechnung(_rechnung: Rechnung) {
    rechnung = _rechnung
}

let globalListeners: Listener[] = []

type RegionKey = keyof Pick<
    Rechnung,
    | "anlagen"
    | "rechnungssteller"
    | "rechnungsdaten"
    | "rechnungsverlauf"
    | "rechnungsempfaenger"
    | "steuervertreter"
    | "lieferinformation"
    | "positionen"
    | "steuerDetails"
    | "gesamtsummen"
    | "zahlungsdaten"
    | "steuerberechnung"
    | "rechnungAbschlaege"
    | "rechnungZuschlaege"
    | "rechnungstexte"
>

const regionalListeners: Record<RegionKey, Listener[]> = {
    anlagen: [],
    rechnungssteller: [],
    rechnungsdaten: [],
    rechnungsverlauf: [],
    rechnungsempfaenger: [],
    steuervertreter: [],
    lieferinformation: [],
    positionen: [],
    steuerDetails: [],
    gesamtsummen: [],
    zahlungsdaten: [],
    steuerberechnung: [],
    rechnungAbschlaege: [],
    rechnungZuschlaege: [],
    rechnungstexte: [],
}

const regionKeys = Object.keys(regionalListeners) as RegionKey[]

function walkPath(path: string, action: (parent: any, property: string) => void) {
    let object: any = rechnung
    const pathElements = path.split(".")
    while (pathElements.length > 1) {
        object = object[pathElements.shift() as Required<keyof Rechnung>]
    }
    action(object, pathElements[0])
}

export const store = {
    push(path: string, item: any) {
        walkPath(path, (parent, property) => {
            parent[property].push(item)
        })
    },
    remove(path: string, index: number) {
        walkPath(path, (parent, property) => {
            parent[property].splice(index, 1)
        })
    },
    changeProperty(path: string, value: unknown) {
        walkPath(path, (parent, propertyname) => {
            if (parent[propertyname] !== value) {
                parent[propertyname] = value
            }
        })
    },
    subscribeToMany(...regions: RegionKey[]) {
        return (listener: Listener) => {
            regions.forEach((r) => (regionalListeners[r] = [...regionalListeners[r], listener]))
            return () => {
                regions.forEach((r) => (regionalListeners[r] = regionalListeners[r].filter((l) => l !== listener)))
            }
        }
    },
    /** use subscribeTo(region) whenever possible !! */
    subscribeToEverything(listener: Listener) {
        globalListeners = [...globalListeners, listener]
        return () => {
            globalListeners = globalListeners.filter((l) => l !== listener)
        }
    },
    getSnapshot() {
        return result
    },
}

export function useStoreRechnung<T extends RegionKey>(...keys: T[]): Pick<Rechnung, T> {
    const { rechnung } = useSyncExternalStore(store.subscribeToMany(...keys), store.getSnapshot)
    return rechnung
}

export function useStoreRechnungEverything(): Rechnung {
    const { rechnung } = useSyncExternalStore(store.subscribeToEverything, store.getSnapshot)
    return rechnung
}

function callRegionalListeners(region: RegionKey) {
    regionalListeners[region]?.forEach((it) => it())
}

function callGlobalListeners() {
    globalListeners.forEach((it) => it())
}

export function emitRegionChange(region: RegionKey) {
    result = { rechnung }
    callGlobalListeners()
    callRegionalListeners(region)
}

export function emitGlobalChange() {
    result = { rechnung }
    callGlobalListeners()
    regionKeys.forEach((it) => emitRegionChange(it))
}

export function emitRegionChangeIfAnyMatch(path: string) {
    for (const it of regionKeys) {
        if (path.indexOf(it) === 0) {
            emitRegionChange(it)
            return
        }
    }
    // if no region change was possible, emit global change
    emitGlobalChange()
}
